import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCustomerAssignUserAccountsContext } from 'selectors/customerAssignUserAccountsSelectors';
import { useAccountSelectionTree } from './useAccountSelectionTree';

import './customer-assign-user-accounts.scss';
import { getAccountsRequest } from 'actions/accountActions';
import { getDetailedUser, assignAccountsToUser } from 'actions/userActions';
import { setCustomerAssignUsersButton, setUserDefaultButton } from 'actions/modalMetaActions';
import { generateRequestBody } from 'features/users/helpers/customer-assign-user-accounts-helper';
import AssignAccounts from 'components/assign-accounts-information/assignAccounts';
import { DropdownSearch } from '@ampol/design-system';
import { trackCustomEvent } from 'utilities/app-insights';

type CustomerAssignUserAccountsProps = {
  userState?: {
    userId: string;
    emailId?: string;
    assignedRoleName?: string;
  };
  canReadWrite: boolean;
  onBackClick?: () => void;
  onSubmit?: (assignedAccounts: string[], role: string | null) => void;
};

const CustomerAssignUserAccounts = ({
  userState,
  onBackClick,
  onSubmit,
  canReadWrite,
}: CustomerAssignUserAccountsProps) => {
  const dispatch = useDispatch();

  const {
    loggedInUser,
    assignedAccountNumber,
    assignedGrandfatherClientNumber,
    createUserLoading,
    assignAccountsToUserLoading,
    detailedUser,
    accounts,
    accountsSize,
    isAccountsLoading,
    isAccountsError,
    assignRolesList,
  } = useSelector(selectCustomerAssignUserAccountsContext);

  const [role, setRole] = useState<string | null>(null);

  const accountsRequest = {
    pageSize: 1200,
    pageNumber: 1,
    sortColumn: 'AccountNumber',
    isSortAsc: false,
    accountSearchTerm: '',
  };

  const isAddModal = !userState;

  const { selectedAccounts, initialSelectedAccounts, selectedAccountsRef, handleCheckboxSelection } =
    useAccountSelectionTree({ accounts, detailedUser, accountsSize, assignedGrandfatherClientNumber });

  const isSelectionChanged = !(
    initialSelectedAccounts.length === selectedAccounts.length &&
    initialSelectedAccounts.every((element) => selectedAccounts.includes(element))
  );
  const isSubmitValid = isSelectionChanged && selectedAccounts.length !== 0 && (isAddModal ? !!role : true);

  useEffect(() => {
    // On mount: fetch the current user's details (edit mode) and the full account list.
    // Intentional mount-only fetch — the user and account list do not need to reload on every
    // render. assignedGrandfatherClientNumber and userState.userId are stable for the modal's
    // lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    !isAddModal && dispatch(getDetailedUser(userState.userId, assignedGrandfatherClientNumber));

    dispatch(
      getAccountsRequest(
        assignedAccountNumber,
        assignedGrandfatherClientNumber,
        accountsRequest.pageSize,
        accountsRequest.pageNumber,
        accountsRequest.sortColumn,
        accountsRequest.isSortAsc,
        accountsRequest.accountSearchTerm
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAddModal || userState.userId !== loggedInUser.userId) {
      dispatch(
        setCustomerAssignUsersButton({
          onClick: () => isSubmitValid && !createUserLoading && !assignAccountsToUserLoading && handleSubmit(),
          inactive: !isSubmitValid || createUserLoading || assignAccountsToUserLoading,
          cancelLabel: isAddModal ? 'Back' : undefined,
          onCancelClick: isAddModal ? onBackClick : undefined,
        })
      );
    } else {
      dispatch(setUserDefaultButton());
    }
  }, [isSubmitValid, createUserLoading, assignAccountsToUserLoading]);

  const sortTable = (sortColumn: string, isAsc: boolean) => {
    dispatch(
      getAccountsRequest(
        assignedAccountNumber,
        assignedGrandfatherClientNumber,
        accountsRequest.pageSize,
        accountsRequest.pageNumber,
        sortColumn,
        isAsc,
        accountsRequest.accountSearchTerm
      )
    );
  };

  const handleSubmit = () => {
    const request = generateRequestBody(initialSelectedAccounts, selectedAccountsRef.current, accounts);
    if (isAddModal) {
      onSubmit?.(request.assignedAccounts, role);
    } else {
      dispatch(
        assignAccountsToUser(
          assignedGrandfatherClientNumber,
          userState.userId,
          request.assignedAccounts,
          request.unassignedAccounts
        )
      );

      trackCustomEvent(
        'Submit Update Assigned Accounts',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Assign in User Detail Modal to Assign Accounts.',
        { selectedUserEmail: userState?.emailId, selectedUserId: userState?.userId }
      );
    }
  };

  return (
    <div className={'customer-assign-user-accounts'}>
      {isAddModal && (
        <div className='customer-assign-user-accounts__dropdown'>
          <DropdownSearch
            value={role}
            onChange={(value: string) => {
              setRole(value);
            }}
            placeholder='Select Role'
            data={assignRolesList.map((x) => ({ label: x.name, value: x.name }))}
            allowClear={false}
          />
        </div>
      )}
      <AssignAccounts
        data={accounts}
        isLoading={isAccountsLoading}
        onColumnSort={sortTable}
        errors={isAccountsError}
        onSelectionChange={handleCheckboxSelection}
        selectedAccounts={
          selectedAccounts ? selectedAccounts.reduce((obj, number) => ({ ...obj, [number]: true }), {}) : {}
        }
        isReadOnly={(!isAddModal && userState.userId === loggedInUser.userId) || !canReadWrite} // if user is the logged-in user, can't change your own assigned accounts
      />
    </div>
  );
};

export default CustomerAssignUserAccounts;
